import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { LINKS } from '@trip-ux/constants';
import { getBrokerageDisplayNames } from './advisorSlice';
import {  ReduxStore } from '@trip-ux/redux-store';

const BROKERAGE = 'BROKERAGE';

type RawSponsor = {
  LHCode: string;
  organizationName: string;
  code: string;
  contract?: {
    sponsorCode: string;
    expiryDate?: string;
  }[];
};

export type SponsorDisplay = {
  displayName: string;
  value: string;
  LHCode: string;
};

/**
 * RTK Query API slice for advisor-related endpoints.
 * Provides cached queries for sponsor listings with automatic refetching and prefetching capabilities.
 */
export const advisorApi = createApi({
  reducerPath: 'advisorApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '',
    prepareHeaders: headers => {
      return headers;
    }
  }),
  tagTypes: ['Sponsors'],
  endpoints: builder => ({
    /**
     * Fetches the list of available sponsor organizations for advisor registration.
     *
     * @returns Array of sponsor objects with displayName, value (org code), and LHCode
     */
    getSponsors: builder.query<SponsorDisplay[], void>({
      queryFn: async (_, { getState, dispatch }) => {
        try {
          const { apiClient } = await import('../api.client');
          const response: RawSponsor[] = await apiClient(dispatch, getState as () => ReduxStore).get(
            `${LINKS.DISTRIBUTION}?category=${BROKERAGE}`
          );

          return { data: getBrokerageDisplayNames(response) };
        } catch (error) {
          const errorResponse = error && typeof error === 'object' && 'response' in error ? error.response : null;
          const errorData =
            errorResponse && typeof errorResponse === 'object' && 'data' in errorResponse ? errorResponse.data : null;

          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: errorData || 'Failed to load sponsors'
            }
          };
        }
      },
      providesTags: ['Sponsors'],
      keepUnusedDataFor: 30
    })
  })
});

export const { useGetSponsorsQuery, usePrefetch } = advisorApi;
